import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Search, AlertTriangle, Check, Minus, Send, Info,
} from 'lucide-react';
import { api } from '../services/apiService';
import { effectiveDealPriceCents } from '../services/dealPricing';
import { RubyLoader } from './RubyLoader';

import { RubyGemMark } from './RubyGemMark';
/**
 * SEND A DEAL TO PARTNERS — the deal-side half of partner offers.
 *
 * Approved mockup: `mockups/deal-send-to-partners.html`.
 *
 * NOTHING ABOUT WHO HAS THIS DEAL IS STORED SEPARATELY. A partner "has" it
 * precisely when a PartnerDealLinks row exists for that partner and deal, so
 * every tick is derived from `listDealOffers` on open. There is deliberately no
 * second record of who was sent what, because a second record is a record that
 * can drift from the links that actually resolve at signup.
 *
 * THE DIALOG ASKS FOR NOTHING. Seats, expiry and commission plan all come off
 * the deal — a second place to type them is a second place for them to
 * disagree, and a bulk send that also wrote terms would silently reset ones
 * negotiated with an individual partner. Those stay on the partner's own
 * Deals & links tab.
 *
 * UNTICKING WITHDRAWS, IT NEVER DELETES. The server pauses the link:
 * `adminDeleteLink` already refuses a link with conversions, and deleting one
 * would orphan the commission trail. Paused links stop resolving at signup, so
 * the offer really is gone from the partner's side while everything they have
 * already earned keeps paying.
 */

interface PartnerRow {
  id: string;
  name: string;
  email: string;
  status: string;
  referralCode?: string;
  isInternalIso?: boolean;
  certification?: { status?: string };
}

interface OfferRow {
  partnerId: string;
  linkId: string;
  code: string;
  status: string;
  displayStatus: string;
  remainingUses: number | null;
  maxUses: number | null;
  usedCount: number;
  signupCount: number;
  expiresAt?: number;
  createdAt?: number;
}

const RUBY = '#DC143C';
const RUBY_DEEP = '#9F1239';

const STATUS_CHIP: Record<string, { cls: string; label: string }> = {
  active: { cls: 'bg-emerald-100 text-emerald-700', label: 'Live' },
  paused: { cls: 'bg-stone-200 text-stone-600', label: 'Withdrawn' },
  expired: { cls: 'bg-amber-100 text-amber-700', label: 'Expired' },
  exhausted: { cls: 'bg-red-100 text-red-700', label: 'Fully claimed' },
};

const money = (cents?: number | null): string =>
  typeof cents === 'number'
    ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const dateStr = (ms?: number | null): string =>
  typeof ms === 'number' && ms > 0
    ? new Date(ms).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

/** Uppercase micro-heading, used for every block in the summary band. */
const Eyebrow: React.FC<{ children: React.ReactNode; tone?: string }> = ({ children, tone }) => (
  <h4
    className="mb-1.5 text-[9.5px] font-bold uppercase tracking-[0.09em]"
    style={{ color: tone || '#a8a29e' }}
  >
    {children}
  </h4>
);

export interface SendDealToPartnersModalProps {
  open: boolean;
  onClose: () => void;
  /** The saved deal. Everything in the summary band is read from it. */
  deal: any;
  /** Package and device catalogues, for naming what the deal contains. */
  packages?: Array<{ id?: string; packageId?: string; name?: string; priceCents?: number; monthlyPriceCents?: number }>;
  /** Called after a successful send, so the page can refresh anything it shows. */
  onSent?: () => void;
}

const SendDealToPartnersModal: React.FC<SendDealToPartnersModalProps> = ({
  open, onClose, deal, packages = [], onSent,
}) => {
  const dealId = deal?.id || deal?.dealId || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [offers, setOffers] = useState<OfferRow[]>([]);

  /** Partner ids ticked right now. Seeded from the live offers on open. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** What was ticked when the dialog opened — the baseline every diff is against. */
  const [initial, setInitial] = useState<Set<string>>(new Set());

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'has' | 'not' | 'iso'>('all');
  /** Per-partner outcomes from the last send, shown in place on the rows. */
  const [outcomes, setOutcomes] = useState<Record<string, { outcome: string; reason?: string }>>({});

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    setOutcomes({});
    try {
      const [partnersRes, offersRes] = await Promise.all([
        api.affiliates.admin.list(),
        api.affiliates.admin.listDealOffers(dealId),
      ]);
      const rows: PartnerRow[] = ((partnersRes as any)?.affiliates || []);
      const live: OfferRow[] = (offersRes?.offers || []) as OfferRow[];
      setPartners(rows);
      setOffers(live);

      // A WITHDRAWN offer counts as NOT sent. Its row still exists so the code
      // can be restored rather than re-minted, but leaving it ticked would tell
      // an operator the partner currently holds an offer they cannot use.
      const held = new Set(live.filter((o) => o.status !== 'paused').map((o) => o.partnerId));
      setSelected(new Set(held));
      setInitial(new Set(held));
    } catch (err: any) {
      setError(err?.message || 'Could not load partners for this deal');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  // Esc closes, but never mid-send: a dismissed dialog during a partial write
  // hides which partners took the offer and which did not.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const offerFor = useCallback(
    (partnerId: string) => offers.find((o) => o.partnerId === partnerId),
    [offers],
  );

  const grant = useMemo(
    () => [...selected].filter((id) => !initial.has(id)),
    [selected, initial],
  );
  const revoke = useMemo(
    () => [...initial].filter((id) => !selected.has(id)),
    [selected, initial],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return partners.filter((p) => {
      const o = offerFor(p.id);
      if (filter === 'has' && !o) return false;
      if (filter === 'not' && o) return false;
      if (filter === 'iso' && !p.isInternalIso) return false;
      if (!q) return true;
      return [p.name, p.email, p.referralCode, o?.code]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [partners, query, filter, offerFor]);

  const counts = useMemo(() => ({
    all: partners.length,
    has: partners.filter((p) => offerFor(p.id)).length,
    not: partners.filter((p) => !offerFor(p.id)).length,
    iso: partners.filter((p) => p.isInternalIso).length,
  }), [partners, offerFor]);

  const toggle = (p: PartnerRow) => {
    // An unapproved partner has no portal to receive an offer into, and the
    // server refuses them — disabled here so that is visible before sending.
    if (p.status !== 'approved') return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
      return next;
    });
  };

  const send = async () => {
    if (!grant.length && !revoke.length) return;
    setSaving(true);
    setError('');
    try {
      const res = await api.affiliates.admin.sendDealOffers(dealId, { grant, revoke });
      const map: Record<string, { outcome: string; reason?: string }> = {};
      for (const r of res.results || []) map[r.partnerId] = { outcome: r.outcome, reason: r.reason };
      setOutcomes(map);

      // Re-read rather than assuming. A partner the server skipped must snap
      // back to unticked, and a resumed link's code has to appear on its row.
      const fresh = await api.affiliates.admin.listDealOffers(dealId);
      const live: OfferRow[] = (fresh?.offers || []) as OfferRow[];
      setOffers(live);
      const held = new Set(live.filter((o) => o.status !== 'paused').map((o) => o.partnerId));
      setSelected(new Set(held));
      setInitial(new Set(held));

      onSent?.();
    } catch (err: any) {
      setError(err?.message || 'Could not send this deal');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const priceCents = effectiveDealPriceCents(deal);
  const packageNames = (deal?.packageIds || [])
    .map((id: string) => packages.find((p) => (p.id || p.packageId) === id)?.name || id);
  const deviceCount = (deal?.deviceSkuIds?.length || 0) + (deal?.devicePackageIds?.length || 0);
  const failures = Object.entries(outcomes).filter(([, v]) => v.outcome === 'failed' || v.outcome === 'skipped');

  return (
    <div className="fixed inset-0 z-[9990] flex items-start justify-center p-4 md:p-5">
      <div
        className="absolute inset-0 bg-stone-900/55 backdrop-blur-[1px]"
        onClick={() => { if (!saving) onClose(); }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sdtp-title"
        className="relative flex w-full max-w-[1320px] flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl"
        style={{ maxHeight: 'calc(100vh - 44px)' }}
      >
        {/* ---------------- head ---------------- */}
        <div className="flex flex-wrap items-start gap-4 border-b border-stone-100 px-5 py-3">
          <div className="min-w-0">
            <h2 id="sdtp-title" className="text-[15px] font-semibold tracking-tight text-stone-900">
              Send this deal to partners
            </h2>
            <p className="mt-0.5 max-w-[64ch] text-xs text-stone-600">
              Everything below is the deal as you saved it. Tick the partners who should be able to sell
              it — each gets their own link and an email. Untick to withdraw.
            </p>
          </div>
          <button
            onClick={() => { if (!saving) onClose(); }}
            aria-label="Close"
            className="ml-auto rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ---------------- what you're sending ---------------- */}
        <div className="border-b border-stone-100 bg-stone-50 px-5 py-3">
          <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
            <span className="text-[15px] font-semibold tracking-tight text-stone-900">
              {deal?.name || 'This deal'}
            </span>
            {deal?.code && (
              <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px] text-stone-600">
                {deal.code}
              </code>
            )}
            <span
              className="ml-auto font-mono text-[17px] font-semibold tabular-nums"
              style={{ color: RUBY_DEEP }}
            >
              {money(priceCents)}<span className="text-xs font-medium text-stone-400">/mo</span>
            </span>
          </div>

          <div className="grid items-start gap-2.5 lg:grid-cols-[1.15fr_1fr_1.5fr]">
            <div className="rounded-xl border border-stone-200 bg-white px-3 py-2.5">
              <Eyebrow>Software packages</Eyebrow>
              <ul className="flex flex-col gap-0.5 text-xs">
                {packageNames.length ? packageNames.slice(0, 4).map((n: string) => (
                  <li key={n} className="truncate text-stone-700">{n}</li>
                )) : <li className="text-stone-400">No packages on this deal</li>}
                {packageNames.length > 4 && (
                  <li className="text-stone-400">+{packageNames.length - 4} more</li>
                )}
                <li className="text-[11px] text-stone-400">
                  {typeof deal?.freeLocationQuota === 'number' ? `${deal.freeLocationQuota} location${deal.freeLocationQuota === 1 ? '' : 's'} included` : 'Locations not set'}
                  {typeof deal?.additionalLocationPriceCents === 'number'
                    ? ` · extra ${money(deal.additionalLocationPriceCents)}/mo` : ''}
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white px-3 py-2.5">
              <Eyebrow>Devices</Eyebrow>
              <p className="text-xs text-stone-700">
                {deviceCount ? `${deviceCount} offered` : <span className="text-stone-400">None on this deal</span>}
              </p>
              <div className="mt-1.5 flex justify-between border-t border-stone-100 pt-1.5 text-xs font-semibold tabular-nums">
                <span className="font-medium text-stone-400">Deal cap</span>
                <span>{deal?.maxUses ? `${deal.maxUses} signups` : 'Unlimited'}</span>
              </div>
            </div>

            <div>
              <Eyebrow>Offer window</Eyebrow>
              <div className="grid grid-cols-2 gap-1.5">
                <div className="rounded-xl border border-stone-200 bg-white px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-stone-500">Links expire</div>
                  <div className="mt-0.5 font-mono text-sm font-semibold tabular-nums">
                    {deal?.expiresAt ? dateStr(new Date(deal.expiresAt).getTime()) : 'Never'}
                  </div>
                  <div className="mt-0.5 text-[10px] text-stone-400">from the deal</div>
                </div>
                <div className="rounded-xl border border-stone-200 bg-white px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-stone-500">Seats each</div>
                  <div className="mt-0.5 font-mono text-sm font-semibold tabular-nums">Unlimited</div>
                  <div className="mt-0.5 text-[10px] text-stone-400">the deal cap applies to all</div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-dashed border-stone-300 bg-white px-3 py-1.5 text-[11px] text-stone-600">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
            <span>
              Links carry no per-partner seat cap — the deal&rsquo;s own{' '}
              <b className="text-stone-800">Max uses</b> already limits the whole offer across everyone.
              To cap or date one partner individually, use their Deals &amp; links tab.
            </span>
          </p>
        </div>

        {/* ---------------- search + filters ---------------- */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-stone-100 px-5 py-2.5">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search partners by name, email or code…"
              className="w-full rounded-lg border border-stone-300 bg-white py-2 pl-9 pr-3 text-[13px] text-stone-800 outline-none placeholder:text-stone-400 focus:border-stone-400"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {([
              ['all', `All ${counts.all}`],
              ['has', `Has this deal ${counts.has}`],
              ['not', `Doesn't ${counts.not}`],
              ['iso', `Internal ISO ${counts.iso}`],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                  filter === key ? 'text-white' : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                }`}
                style={filter === key ? { background: RUBY, borderColor: RUBY } : undefined}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-5 py-2.5 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* Partial-failure report. A tally alone would say "18 sent" and leave
            the other two invisible. */}
        {failures.length > 0 && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-xs text-amber-800">
            <p className="font-semibold">{failures.length} partner{failures.length > 1 ? 's were' : ' was'} not changed:</p>
            <ul className="mt-1 space-y-0.5">
              {failures.slice(0, 5).map(([pid, v]) => (
                <li key={pid}>
                  {partners.find((p) => p.id === pid)?.name || pid} — {v.reason || v.outcome}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ---------------- partner rows ---------------- */}
        <div className="min-h-[150px] flex-1 overflow-y-auto">
          {loading ? (
            <RubyLoader variant="section" className="py-12" />
          ) : !visible.length ? (
            <p className="py-12 text-center text-sm text-stone-400">
              {partners.length ? 'No partners match this search.' : 'No partners yet.'}
            </p>
          ) : visible.map((p) => {
            const o = offerFor(p.id);
            const ticked = selected.has(p.id);
            const had = initial.has(p.id);
            const dropping = had && !ticked;
            const eligible = p.status === 'approved';
            const chip = o ? (STATUS_CHIP[o.displayStatus] || STATUS_CHIP.active) : null;

            return (
              <div
                key={p.id}
                onClick={() => toggle(p)}
                className={`flex items-start gap-3 border-b border-stone-100 px-5 py-2.5 ${
                  !eligible ? 'opacity-55'
                    : dropping ? 'cursor-pointer bg-amber-50'
                    : o ? 'cursor-pointer bg-rose-50/40 hover:bg-rose-50/70'
                    : 'cursor-pointer hover:bg-stone-50'
                }`}
              >
                <span
                  aria-hidden
                  className={`mt-0.5 grid h-[18px] w-[18px] flex-none place-items-center rounded-[5px] border-[1.5px] ${
                    dropping ? 'border-amber-300 bg-amber-100 text-amber-700'
                      : ticked ? 'border-transparent text-white'
                      : 'border-stone-300 bg-white'
                  }`}
                  style={ticked && !dropping ? { background: RUBY, borderColor: RUBY } : undefined}
                >
                  {dropping ? <Minus className="h-3 w-3" /> : ticked ? <Check className="h-3 w-3" /> : null}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-stone-900">{p.name}</span>
                    {p.isInternalIso && (
                      <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: '#ffe4e6', color: RUBY_DEEP }}>
                        Internal ISO
                      </span>
                    )}
                    {p.status !== 'approved' && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold capitalize text-amber-700">
                        {p.status}
                      </span>
                    )}
                    {p.certification?.status && p.certification.status !== 'passed' && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                        Not certified
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-600">{p.email}</div>

                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-stone-400">
                    {o ? (
                      <>
                        {chip && <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${chip.cls}`}>{chip.label}</span>}
                        <code className="font-mono text-[11px] text-stone-600">{o.code}</code>
                        {o.createdAt ? <span>· sent {dateStr(o.createdAt)}</span> : null}
                      </>
                    ) : eligible ? (
                      <span>No link for this deal yet</span>
                    ) : (
                      <span>Not approved yet, so they have no portal to receive an offer into.</span>
                    )}
                  </div>

                  {dropping && (
                    <p className="mt-1 flex items-start gap-1.5 text-[11.5px] text-amber-700">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>
                        Will be withdrawn — the link is paused, not deleted, and {p.name.split(' ')[0]} is
                        emailed.{o && o.usedCount > 0 ? ` Their ${o.usedCount} existing conversion${o.usedCount > 1 ? 's keep' : ' keeps'} paying.` : ''}
                      </span>
                    </p>
                  )}
                  {outcomes[p.id]?.outcome === 'created' && (
                    <p className="mt-1 text-[11.5px] font-medium text-emerald-700">Offer sent.</p>
                  )}
                  {outcomes[p.id]?.outcome === 'resumed' && (
                    <p className="mt-1 text-[11.5px] font-medium text-emerald-700">Offer restored — same code.</p>
                  )}
                  {outcomes[p.id]?.outcome === 'revoked' && (
                    <p className="mt-1 text-[11.5px] font-medium text-stone-500">Withdrawn.</p>
                  )}
                </div>

                <div className="w-[132px] flex-none text-right text-[11.5px] text-stone-500">
                  {o ? (
                    <>
                      <div>
                        <b className="tabular-nums text-stone-800">{o.usedCount || 0}</b>
                        {o.maxUses === null ? ' paid' : ` of ${o.maxUses} seats used`}
                      </div>
                      <div className="text-stone-400">
                        {o.expiresAt ? `expires ${dateStr(o.expiresAt)}` : 'no expiry'}
                      </div>
                    </>
                  ) : eligible ? (
                    <>
                      <div className="text-stone-400">unlimited seats</div>
                      <div className="text-stone-400">
                        {deal?.expiresAt ? `expires ${dateStr(new Date(deal.expiresAt).getTime())}` : 'no expiry'}
                      </div>
                    </>
                  ) : <span className="text-stone-300">—</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* ---------------- foot ---------------- */}
        <div className="flex flex-wrap items-center gap-3.5 border-t border-stone-200 bg-stone-50 px-5 py-2.5">
          <div className="flex flex-wrap gap-3.5 text-[12.5px] text-stone-600">
            <span><b className="text-stone-900">{grant.length}</b> new offer{grant.length === 1 ? '' : 's'}</span>
            <span className="text-amber-700"><b>{revoke.length}</b> to withdraw</span>
            <span><b className="text-stone-900">{initial.size - revoke.length}</b> unchanged</span>
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => { if (!saving) onClose(); }}
              className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-600 hover:bg-stone-50"
            >
              Close
            </button>
            <button
              onClick={send}
              disabled={saving || loading || (!grant.length && !revoke.length)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
              style={{ background: RUBY }}
            >
              {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send className="h-3.5 w-3.5" />}
              {saving ? 'Sending…'
                : !grant.length && !revoke.length ? 'No changes'
                : `Send ${grant.length}${revoke.length ? `, withdraw ${revoke.length}` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendDealToPartnersModal;
